/**
 * Centralized member display name utilities.
 *
 * Single source of truth for formatting member names and generating initials.
 * Imported by components, hooks, and context providers across the app.
 */

/** Fallback for real users whose profile data is incomplete. */
export const UNKNOWN_USER = 'Unknown User';

interface MemberLike {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}

/** Parse and trim name fields, returning null for empty strings. */
function parseNames(
  firstName?: string | null,
  lastName?: string | null,
): { first: string | undefined; last: string | undefined } {
  return {
    first: firstName?.trim() || undefined,
    last: lastName?.trim() || undefined,
  };
}

/**
 * Format a member's display name from profile fields.
 *
 * Fallback chain: "First Last" -> "First" -> email -> UNKNOWN_USER
 */
export function getDisplayName(member: MemberLike): string {
  const { first, last } = parseNames(member.first_name, member.last_name);

  if (first && last) {
    return `${first} ${last}`;
  }
  if (first) {
    return first;
  }
  return member.email || UNKNOWN_USER;
}

/**
 * Generate initials from member profile fields for avatar fallbacks.
 *
 * Fallback chain: "FL" -> "F" -> first char of email -> "U"
 */
export function getInitials(
  firstName?: string | null,
  lastName?: string | null,
  email?: string | null,
): string {
  const { first, last } = parseNames(firstName, lastName);

  if (first && last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  }
  if (first) {
    return first.charAt(0).toUpperCase();
  }
  if (email) {
    return email.charAt(0).toUpperCase();
  }
  return 'U';
}
